import type { ThemeTokenAlias } from "../theme-engine.js";

export interface BrandLogoAsset {
  src: string;
  alt: string;
}

export interface BrandLogoSet {
  appSwitcher: BrandLogoAsset;
  wordmark: BrandLogoAsset;
  compact: BrandLogoAsset;
}

export interface BrandTypography {
  title: string;
  body: string;
  number: string;
  marker: string;
}

export interface BrandGradients {
  primary: string;
  marker: string;
  surface: string;
}

export interface BrandPalette {
  primary: string;
  accent: string;
  background: string;
  foreground: string;
  backgroundDark: string;
  foregroundDark: string;
}

export interface BrandMetadata {
  shortName: string;
  tagline: string;
  logos: BrandLogoSet;
  typography: BrandTypography;
  gradients: BrandGradients;
  palette: BrandPalette;
}

export interface ModeAwareThemeSource {
  id: "bears";
  label: string;
  description: string;
  brand: BrandMetadata;
  lightTokens: Partial<Record<ThemeTokenAlias, string>>;
  darkTokens: Partial<Record<ThemeTokenAlias, string>>;
}

export const chicagoBearsThemeConfig = {
  id: "bears",
  label: "Bears",
  description: "Chicago Bears brand theme derived from the official brand guidelines color palette and shell-safe semantic token mappings.",
  brand: {
    shortName: "Bears",
    tagline: "Monsters of the Midway",
    logos: {
      appSwitcher: {
        src: "https://static.www.nfl.com/t_q-best/league/api/clubs/logos/CHI",
        alt: "Chicago Bears logo"
      },
      wordmark: {
        src: "https://static.www.nfl.com/t_q-best/league/api/clubs/logos/CHI",
        alt: "Chicago Bears wordmark"
      },
      compact: {
        src: "https://static.www.nfl.com/t_q-best/league/api/clubs/logos/CHI",
        alt: "Chicago Bears compact logo"
      }
    },
    typography: {
      title: "\"Industry\", \"Oswald\", \"Arial Narrow\", sans-serif",
      body: "\"Inter\", \"Segoe UI\", Arial, sans-serif",
      number: "\"Barlow Condensed\", \"Arial Narrow\", sans-serif",
      marker: "\"Industry\", \"Oswald\", \"Arial Narrow\", sans-serif"
    },
    gradients: {
      primary: "linear-gradient(135deg, #0B162A 0%, #1B2D4B 58%, #E64100 100%)",
      marker: "linear-gradient(90deg, #E64100 0%, #0B162A 100%)",
      surface: "linear-gradient(180deg, rgba(230,65,0,0.12) 0%, rgba(11,22,42,0) 100%)"
    },
    palette: {
      primary: "#0B162A",
      accent: "#E64100",
      background: "#FFFFFF",
      foreground: "#0B162A",
      backgroundDark: "#111927",
      foregroundDark: "#F3F4F6"
    }
  },
  lightTokens: {
    "color.action": "#E64100",
    "color.backdrop": "rgba(11, 22, 42, 0.72)",
    "color.surface": "#FFFFFF",
    "color.text": "#0B162A",
    "sidebar.surface": "#0B162A",
    "sidebar.surfaceHover": "#1B2D4B",
    "sidebar.text": "#FFFFFF",
    "sidebar.textMuted": "#C7D0DE"
  },
  darkTokens: {
    "color.action": "#FF5C1A",
    "color.backdrop": "rgba(1, 7, 18, 0.82)",
    "color.surface": "#111927",
    "color.text": "#F3F4F6",
    "sidebar.surface": "#08101F",
    "sidebar.surfaceHover": "#14233A",
    "sidebar.text": "#F9FAFB",
    "sidebar.textMuted": "#D1D5DB"
  }
} as const satisfies ModeAwareThemeSource;
